import { useEffect, useState } from 'react';
import { useDocStore } from '../store/docStore';

function systemPrefersDark(): boolean {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

/**
 * Resolve the effective dark/light mode from the store's `themePref` and the
 * typography theme from the store's `theme`, and apply BOTH to the document root:
 * - `data-theme` = `light` | `dark` — the COLOR MODE:
 *   - `system` → follow `prefers-color-scheme` live.
 *   - `light` / `dark` → forced.
 * - `data-theme-name` = `sans-contrast` | `sans-reader` | `serif-contrast` |
 *   `serif-reader` — the TYPOGRAPHY theme (prose typeface + palette character).
 *
 * The two axes are independent: the 8 token sets in `index.css` are keyed by
 * `[data-theme-name='…'][data-theme='…']`.
 *
 * Returns the resolved boolean (true = dark) so editors can theme-match.
 */
export function useSystemTheme(): boolean {
  const themePref = useDocStore((s) => s.themePref);
  const theme = useDocStore((s) => s.theme);
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Track the OS scheme regardless of preference, so flipping back to `system`
  // immediately reflects the current OS value.
  useEffect(() => {
    const mq = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = (): void => setSystemDark(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const dark = themePref === 'system' ? systemDark : themePref === 'dark';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  }, [dark]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme-name', theme);
  }, [theme]);

  return dark;
}
